import type { CSSProperties, ReactNode } from 'react';
import type { Board, Notes } from '../hooks/useGameBoard';
import { useI18n } from '../i18n';

/** Bir hücrenin otomatik adayları — satır/sütun/kutuda kullanılmayan rakamlar. */
function calcCandidates(board: Board, r: number, c: number): number[] {
  const used = new Set<number>();
  for (let i = 0; i < 9; i++) {
    if (board[r]?.[i]) used.add(board[r][i] as number);
    if (board[i]?.[c]) used.add(board[i][c] as number);
  }
  const br = Math.floor(r / 3) * 3;
  const bc = Math.floor(c / 3) * 3;
  for (let dr = 0; dr < 3; dr++)
    for (let dc = 0; dc < 3; dc++)
      if (board[br + dr]?.[bc + dc]) used.add(board[br + dr][bc + dc] as number);
  return [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(n => !used.has(n));
}

interface GameGridProps {
  board: Board;
  notes: Notes;
  /** Approach A: açıkken tüm boş hücrelerde canlı otomatik adaylar (manuel notlar gizli). */
  showCandidates?: boolean;
  /** Multiplayer: rakip hücrelerini de hesaba katan önceden hesaplanmış adaylar. */
  candidatesOverride?: number[][][] | null;
  isPaused: boolean;
  getCellClass: (r: number, c: number) => string;
  onCellClick: (r: number, c: number) => void;
  onResumeFromPause: () => void;
  onNewGameFromPause?: () => void;
  pauseTitle?: string;
  pauseSubtitle?: string;
  getCellStyle?: (r: number, c: number) => CSSProperties | undefined;
  getCellValueStyle?: (r: number, c: number) => CSSProperties | undefined;
  renderCellExtra?: (r: number, c: number) => ReactNode;
}

export function GameGrid({
  board,
  notes,
  showCandidates,
  candidatesOverride,
  isPaused,
  getCellClass,
  onCellClick,
  onResumeFromPause,
  onNewGameFromPause,
  pauseTitle,
  pauseSubtitle,
  getCellStyle,
  getCellValueStyle,
  renderCellExtra,
}: GameGridProps) {
  const { t } = useI18n();

  return (
    <div className="game-board" style={{ position: 'relative' }}>
      {isPaused && (
        <div className="board-pause-overlay" onClick={onResumeFromPause}>
          <span>{pauseTitle || t.game.paused}</span>
          <small>{pauseSubtitle || t.game.resumeHint}</small>
          {onNewGameFromPause && (
            <button
              className="pause-newgame-btn"
              onClick={(e) => { e.stopPropagation(); onNewGameFromPause(); }}
            >
              {t.common.newGame}
            </button>
          )}
        </div>
      )}
      <table className="game-grid">
        <tbody>
          {Array.from({ length: 9 }, (_, r) => (
            <tr key={r}>
              {Array.from({ length: 9 }, (_, c) => (
                <td
                  key={c}
                  className={getCellClass(r, c)}
                  style={getCellStyle?.(r, c)}
                  onClick={() => onCellClick(r, c)}
                >
                  {board[r]?.[c] != null && board[r][c] !== 0 ? (
                    <span className="cell-value" style={getCellValueStyle?.(r, c)}>{board[r][c]}</span>
                  ) : (() => {
                    // Approach A: Adaylar açıkken canlı otomatik adaylar; kapalıyken manuel notlar.
                    const cellCands = showCandidates
                      ? (candidatesOverride?.[r]?.[c] ?? calcCandidates(board, r, c))
                      : (notes[r]?.[c] ?? []);
                    return cellCands.length > 0 ? (
                      <>
                        <div className="cell-notes">
                          {[1,2,3,4,5,6,7,8,9].map(n => (
                            <span key={n} className={`note ${cellCands.includes(n) ? 'active' : ''}`}>
                              {cellCands.includes(n) ? n : ''}
                            </span>
                          ))}
                        </div>
                        {renderCellExtra?.(r, c)}
                      </>
                    ) : (
                      renderCellExtra?.(r, c) ?? null
                    );
                  })()}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
